import { ticketStatusMeta } from "./bettorsDeskHelpers";

export const TICKET_STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Open" },
  { value: "won", label: "Won" },
  { value: "paid", label: "Paid" },
  { value: "lost", label: "Lost" },
  { value: "voided", label: "Voided" },
  { value: "refunded", label: "Refunded" },
];

export const TICKET_SORTS = [
  { value: "recent", label: "Recent" },
  { value: "stake_desc", label: "Top stake" },
  { value: "payout_desc", label: "Top payout" },
  { value: "odds_desc", label: "Top odds" },
];

export const formatTicketCount = (n) => {
  const count = Number(n) || 0;
  return `${count} ticket${count === 1 ? "" : "s"}`;
};

export const ticketPlacedDateKey = (row) => {
  if (!row?.placedAt) return "unknown";
  const d = new Date(row.placedAt);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const ticketListSummary = (ticket) => {
  const meta = ticketStatusMeta(ticket?.status);
  const legCount = Number(ticket?.legCount) || 0;
  const openLegs = Number(ticket?.openLegs) || 0;
  const legsLabel =
    legCount > 1
      ? openLegs > 0
        ? `${openLegs} open · ${legCount} legs`
        : `${legCount} legs`
      : legCount === 1
      ? "Single"
      : null;
  return { meta, legsLabel };
};

export const normalizeHouseTicketRow = (row) => ({
  ticketId: row.ticketId || row._id,
  userId: row.userId,
  username: row.username || "Bettor",
  placedAt: row.placedAt,
  status: row.status || "pending",
  stake: Number(row.stake ?? row.amountBet ?? 0),
  potentialPayout: Number(row.potentialPayout ?? row.potentialWinnings ?? 0),
  totalOdds: Number(row.totalOdds ?? row.totalOddsValue ?? 0),
  legCount: Number(row.legCount) || 0,
  openLegs: Number(row.openLegs) || 0,
  legs: Array.isArray(row.legs) ? row.legs : [],
  previewLabel: row.previewLabel || null,
});

export const normalizeHouseTicketList = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map(normalizeHouseTicketRow);

/** Pulse from GET /api/bet/:houseId/tickets filteredPulse — aggregated from Betting docs. */
export const normalizeTicketPulse = (pulse) => {
  if (!pulse || typeof pulse !== "object") {
    return { loaded: false };
  }

  return {
    loaded: true,
    ticketCount: Number(pulse.ticketCount) || 0,
    minStake: pulse.minStake != null ? Number(pulse.minStake) : null,
    maxStake: pulse.maxStake != null ? Number(pulse.maxStake) : null,
    avgStake: pulse.avgStake != null ? Number(pulse.avgStake) : null,
    sumStake: Number(pulse.sumStake) || 0,
    avgOdds: pulse.avgOdds != null ? Number(pulse.avgOdds) : null,
    avgLegCount: pulse.avgLegCount != null ? Number(pulse.avgLegCount) : null,
    pendingLiability: Number(pulse.pendingLiability) || 0,
    openTicketCount: Number(pulse.openTicketCount) || 0,
    earliestPlacedAt: pulse.earliestPlacedAt ?? null,
    latestPlacedAt: pulse.latestPlacedAt ?? null,
    byStatus: pulse.byStatus && typeof pulse.byStatus === "object" ? pulse.byStatus : null,
    dailyTrend: Array.isArray(pulse.dailyTrend) ? pulse.dailyTrend : null,
    repeatBettorCount: Number(pulse.repeatBettorCount) || 0,
    distinctBettors: Number(pulse.distinctBettors) || 0,
    avgTicketsPerBettor:
      pulse.avgTicketsPerBettor != null ? Number(pulse.avgTicketsPerBettor) : null,
  };
};

export const TICKET_STATUS_ORDER = [
  "pending",
  "won",
  "paid",
  "lost",
  "voided",
  "refunded",
];

export const sumSeries = (values = []) =>
  (values || []).reduce((sum, value) => sum + (Number(value) || 0), 0);

export const maxSeriesValue = (values) =>
  Math.max(0, ...(values || []).map((v) => Number(v) || 0));

/* ─── "Risk desk" analytics — computed client-side from stored data ─── */

/** Fixed odds buckets describing what kind of book the operator is writing. */
export const ODDS_BUCKETS = [
  { key: "1-2", label: "1–2×", min: 1, max: 2 },
  { key: "2-3", label: "2–3×", min: 2, max: 3 },
  { key: "3-5", label: "3–5×", min: 3, max: 5 },
  { key: "5-10", label: "5–10×", min: 5, max: 10 },
  { key: "10+", label: "10×+", min: 10, max: Infinity },
];

/** Histogram of totalOdds from loaded ticket rows. */
export const oddsHistogram = (rows = []) => {
  const values = (Array.isArray(rows) ? rows : [])
    .map((r) => Number(r?.totalOdds) || 0)
    .filter((v) => v > 0);
  if (!values.length) return { loaded: false };

  const buckets = ODDS_BUCKETS.map((b) => ({ ...b, count: 0 }));
  for (const v of values) {
    const idx = buckets.findIndex((b) => v >= b.min && v < b.max);
    buckets[idx === -1 ? buckets.length - 1 : idx].count += 1;
  }

  return {
    loaded: true,
    buckets,
    maxCount: Math.max(...buckets.map((b) => b.count), 1),
    sample: values.length,
  };
};

/** Fraction (0..1) across the fixed odds axis where an odds value sits. */
export const oddsAxisPosition = (value) => {
  const v = Number(value);
  if (!Number.isFinite(v) || v <= 1) return 0;
  const idx = ODDS_BUCKETS.findIndex(
    (b) => v >= b.min && (b.max === Infinity || v < b.max)
  );
  const i = idx === -1 ? ODDS_BUCKETS.length - 1 : idx;
  const b = ODDS_BUCKETS[i];
  const inner =
    b.max === Infinity
      ? Math.min(1, (v - b.min) / b.min)
      : (v - b.min) / (b.max - b.min);
  return Math.min(1, (i + Math.min(Math.max(inner, 0), 1)) / ODDS_BUCKETS.length);
};

/**
 * House-perspective status meta: lost tickets mean the house keeps the stake
 * (green for the house), won/paid mean the house pays out (red).
 */
export const HOUSE_FLOW = {
  lost: { label: "Lost", houseLabel: "House keeps stake", color: "#46c479", tone: "keep" },
  pending: { label: "Open", houseLabel: "Still at risk", color: "#64b5f6", tone: "open" },
  won: { label: "Won", houseLabel: "House owes payout", color: "#ef5350", tone: "pay" },
  paid: { label: "Paid", houseLabel: "Winnings paid out", color: "#e57373", tone: "pay" },
  voided: { label: "Voided", houseLabel: "No action", color: "#90a4ae", tone: "muted" },
  refunded: { label: "Refunded", houseLabel: "Stake returned", color: "#78909c", tone: "muted" },
};

export const HOUSE_FLOW_ORDER = ["lost", "pending", "won", "paid", "voided", "refunded"];

export const houseFlowMeta = (status = "") =>
  HOUSE_FLOW[String(status).toLowerCase()] || {
    label: status || "Unknown",
    houseLabel: "—",
    color: "#90a4ae",
    tone: "muted",
  };

/** House keep-rate over settled tickets (lost vs won+paid). */
export const houseKeepRate = (byStatus = {}) => {
  const lost = Number(byStatus?.lost) || 0;
  const won = Number(byStatus?.won) || 0;
  const paid = Number(byStatus?.paid) || 0;
  const settled = lost + won + paid;
  if (settled <= 0) return null;
  return Math.round((lost / settled) * 100);
};

export const LIABILITY_CONCENTRATION_RISK = 0.35;

/** How much of the total open liability sits on the single biggest ticket. */
export const liabilityConcentration = (pendingLiability, topTicket) => {
  const total = Number(pendingLiability) || 0;
  const top = Number(topTicket?.liability) || 0;
  if (total <= 0 || top <= 0) return { loaded: false };
  const share = Math.min(1, top / total);
  return {
    loaded: true,
    share,
    sharePct: Math.round(share * 100),
    risky: share > LIABILITY_CONCENTRATION_RISK,
  };
};

/** Set of ticketIds flagged as top open liabilities by the dashboard. */
export const highLiabilityIds = (topByLiability = []) =>
  new Set(
    (Array.isArray(topByLiability) ? topByLiability : [])
      .map((t) => String(t?.ticketId || ""))
      .filter(Boolean)
  );
